/**
 * LastWeekBoxes - the Dashboard's four "last week" KPI boxes and the window
 * each opens (the rules are in lastWeek.js):
 *
 *   Quoted last week     opportunities and projects quoted in the week
 *   PO's last week       POs issued in the week
 *   $ collected          the payments received in the week, from the invoice
 *                        ledger (GET /api/finance/collected, 10/11/26)
 *   Invoices last week   the invoices dated in the week, from the invoice
 *                        ledger (GET /api/finance/invoices); re-read live
 *                        when an invoice or a project's invoicing changes
 *
 * Figures typed by hand (10/9/26, GET/PUT /api/dashboard-weeks) replace what
 * GlobalOps counted for that week and box; the window shows both. Access:
 * the Quoted / PO's / Invoices boxes are the 'week-boxes' area, $ collected
 * 'week-collected' - Hidden drops the box, View sees it, Edit types figures.
 * Each window steps through the weeks (back in time, up to this week so far).
 *
 * useLastWeekBoxes() gives DashboardView the boxes as KPI entries (drawn by
 * KpiCard, so they look like the rest of the row) plus the window to render;
 * each box is its own Dashboard widget and carries the This / Last week
 * switch on its date line (10/12/26: the boxes can be moved apart). Quoted and POs use the projects the Dashboard already has
 * (AppContext allProjects), so they show exactly what the person may read.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { fmtDate, fmtMoney } from '../../utils/format';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { useMyAccess } from '../../hooks/useMyAccess';
import {
  DASH_TIME_ZONE,
  addDays,
  byOwner,
  collectedSummary,
  dayIn,
  invoiceSummary,
  lastWeekRange,
  posIn,
  quotedIn,
  rangeLabel,
  rangeLongLabel,
  recordHash,
  touchesInvoices,
} from './lastWeek';

const MUTED = '#6B7168';
const money = (n) => fmtMoney(Number(n) || 0);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The invoices of a week from the server: { state: 'loading' | 'ok' | 'failed' | 'denied', data }. */
function useInvoices(range, skip = false) {
  const [out, setOut] = useState({ state: 'loading', data: null });
  const [reads] = useState(latestReads);
  const load = useCallback(() => {
    if (skip) return Promise.resolve();
    const ticket = reads.start();
    const url = `/api/finance/invoices?from=${range.from}&to=${range.to}`;
    // a fetch that cannot even start (no network layer) reads as a failed load
    return Promise.resolve()
      .then(() => fetch(url))
      .then(async (r) => {
        if (r.status === 401 || r.status === 403) {
          if (reads.show(ticket)) setOut({ state: 'denied', data: null });
          return;
        }
        const data = r.ok ? await r.json() : null;
        if (!reads.show(ticket)) return;
        // a failed RE-read keeps what is shown; only a first load says it failed
        if (data && Array.isArray(data.invoices)) setOut({ state: 'ok', data });
        else setOut((was) => (was.state === 'ok' ? was : { state: 'failed', data: null }));
      })
      .catch(() => setOut((was) => (was.state === 'ok' ? was : { state: 'failed', data: null })));
  }, [range.from, range.to, reads, skip]);
  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(['projects', 'finance-invoices'], load, { match: touchesInvoices });
  return out;
}

/** The payments received in a week: { state: 'loading' | 'ok' | 'failed' | 'denied', data }. */
function useCollected(range, skip = false) {
  const [out, setOut] = useState({ state: 'loading', data: null });
  const [reads] = useState(latestReads);
  const load = useCallback(() => {
    if (skip) return Promise.resolve();
    const ticket = reads.start();
    return Promise.resolve()
      .then(() => fetch(`/api/finance/collected?from=${range.from}&to=${range.to}`))
      .then(async (r) => {
        if (r.status === 401 || r.status === 403) {
          if (reads.show(ticket)) setOut({ state: 'denied', data: null });
          return;
        }
        const data = r.ok ? await r.json() : null;
        if (!reads.show(ticket)) return;
        if (data && Array.isArray(data.payments)) setOut({ state: 'ok', data });
        else setOut((was) => (was.state === 'ok' ? was : { state: 'failed', data: null }));
      })
      .catch(() => setOut((was) => (was.state === 'ok' ? was : { state: 'failed', data: null })));
  }, [range.from, range.to, reads, skip]);
  useEffect(() => {
    load();
  }, [load]);
  // a payment recorded, changed or removed on the Finance Invoice tab
  useLiveRefresh('finance-invoices', load);
  return out;
}

/** The figures typed for a week: { figures: { box: {...} }, reload }. Unreadable = none. */
function useWeekFigures(week, skip = false) {
  const [figures, setFigures] = useState({});
  const [reads] = useState(latestReads);
  const load = useCallback(() => {
    if (skip) return Promise.resolve();
    const ticket = reads.start();
    return Promise.resolve()
      .then(() => fetch(`/api/dashboard-weeks?week=${week}`))
      .then(async (r) => {
        const data = r.ok ? await r.json() : null;
        if (reads.show(ticket)) setFigures((data && data.figures) || {});
      })
      .catch(() => {});
  }, [week, reads, skip]);
  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh('dashboard-weeks', load);
  return { figures, reload: load };
}

const INPUT = { border: '1px solid #D8DED4', borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' };

/**
 * The figures typed over a box for the window's week: what is shown, what
 * GlobalOps counted, and - for Edit - the form to set or clear them.
 * `counted` = { count, total } or null ($ collected counts nothing).
 */
function FigureBar({ box, week, figure, counted, canEdit, countWord, onSaved }) {
  const [form, setForm] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const hasCount = box !== 'collected';
  const send = async (method, body) => {
    setBusy(true);
    setErr('');
    try {
      const r = await fetch(`/api/dashboard-weeks/${week}/${box}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(d.error || 'Could not save - try again.');
        return;
      }
      setForm(null);
      await onSaved();
    } catch {
      setErr('Could not save - try again.');
    } finally {
      setBusy(false);
    }
  };
  const open = () =>
    setForm({
      count: figure && figure.count != null ? String(figure.count) : counted ? String(counted.count) : '',
      total: figure ? String(figure.total) : counted ? String(counted.total) : '',
      note: (figure && figure.note) || '',
    });
  const countedLine = counted && `GlobalOps counted ${plural(counted.count, countWord)} · ${money(counted.total)}`;
  return (
    <div
      style={{
        border: `1px solid ${figure ? '#B9D8B0' : '#ECECEC'}`,
        background: figure ? '#F3F9F0' : '#FAFBFC',
        borderRadius: 10,
        padding: '10px 12px',
        marginBottom: 12,
        fontSize: 11.5,
        color: '#333',
      }}
    >
      {figure ? (
        <div>
          <strong>Entered:</strong> {hasCount && figure.count != null ? `${plural(figure.count, countWord)} · ` : ''}
          {money(figure.total)}
          <span style={{ color: MUTED }}>
            {' '}
            - {figure.setBy || 'someone'}
            {figure.setAt ? `, ${fmtDate(figure.setAt)}` : ''}
            {figure.note ? ` · ${figure.note}` : ''}
          </span>
          {countedLine && <div style={{ color: MUTED, marginTop: 3 }}>{countedLine} (the list below); the box shows what was entered.</div>}
        </div>
      ) : (
        <div style={{ color: MUTED }}>{countedLine ? `${countedLine}.` : 'Nothing entered for this week.'}</div>
      )}
      {canEdit && !form && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            className="go-focus"
            onClick={open}
            style={{ ...INPUT, cursor: 'pointer', fontWeight: 700, background: '#fff', color: '#1B5E20' }}
          >
            {figure ? 'Change the numbers' : box === 'collected' ? 'Correct the amount' : 'Correct the numbers'}
          </button>
          {figure && (
            <button
              type="button"
              disabled={busy}
              onClick={() => send('DELETE')}
              style={{ ...INPUT, cursor: 'pointer', background: '#fff', color: '#555' }}
            >
              {counted ? "Use GlobalOps' count" : 'Clear'}
            </button>
          )}
        </div>
      )}
      {form && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send('PUT', hasCount ? form : { total: form.total, note: form.note });
          }}
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}
        >
          {hasCount && (
            <label style={{ display: 'grid', gap: 3, fontSize: 10, color: MUTED }}>
              Number of {countWord}s
              <input
                aria-label={`Number of ${countWord}s`}
                inputMode="numeric"
                value={form.count}
                onChange={(e) => setForm({ ...form, count: e.target.value })}
                style={{ ...INPUT, width: 90 }}
              />
            </label>
          )}
          <label style={{ display: 'grid', gap: 3, fontSize: 10, color: MUTED }}>
            Total ($)
            <input
              aria-label="Total ($)"
              inputMode="decimal"
              value={form.total}
              onChange={(e) => setForm({ ...form, total: e.target.value })}
              style={{ ...INPUT, width: 130 }}
            />
          </label>
          <label style={{ display: 'grid', gap: 3, fontSize: 10, color: MUTED, flex: 1, minWidth: 160 }}>
            Note (optional)
            <input aria-label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} style={INPUT} />
          </label>
          <button
            type="submit"
            disabled={busy}
            style={{ ...INPUT, cursor: 'pointer', fontWeight: 700, background: '#1B5E20', color: '#fff', borderColor: '#1B5E20' }}
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setForm(null);
              setErr('');
            }}
            style={{ ...INPUT, cursor: 'pointer', background: '#fff' }}
          >
            Cancel
          </button>
        </form>
      )}
      {err && (
        <div role="alert" style={{ color: '#C62828', marginTop: 6 }}>
          {err}
        </div>
      )}
    </div>
  );
}

/* ── the windows ─────────────────────────────────────────────────────── */

const TH = {
  textAlign: 'left',
  padding: '7px 10px',
  fontSize: 9,
  fontWeight: 700,
  color: '#888',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  background: '#FAFBFC',
  borderBottom: '1px solid #ECECEC',
  whiteSpace: 'nowrap',
};
const TD = { padding: '7px 10px', fontSize: 11, color: '#333', borderBottom: '1px solid #F4F6F0', verticalAlign: 'top' };
const NUM = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const MONO = { fontFamily: 'JetBrains Mono, Consolas, monospace', fontWeight: 700, color: '#1B5E20', fontSize: 10.5 };
const TOTAL = { ...TD, fontWeight: 800, color: '#0D0D0D', background: '#F6F9F3', borderTop: '1px solid #E5E9DD', borderBottom: 'none' };

/** A table whose rows open their record (a row with no `href` is not a link). */
function RecordTable({ columns, rows, footer, label, onOpen }) {
  return (
    <div style={{ border: '1px solid #ECECEC', borderRadius: 10, overflow: 'auto' }}>
      <table aria-label={label} style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ ...TH, ...(c.num ? { textAlign: 'right' } : null) }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const open = r.href ? () => onOpen(r.href) : null;
            return (
              <tr
                key={r.key}
                tabIndex={open ? 0 : undefined}
                title={open ? `Open ${r.title}` : undefined}
                onClick={open || undefined}
                onKeyDown={
                  open
                    ? (e) => {
                        if (e.key === 'Enter') open();
                      }
                    : undefined
                }
                className={open ? 'go-focus' : undefined}
                style={{ background: i % 2 ? '#FAFBF7' : '#fff', cursor: open ? 'pointer' : 'default' }}
              >
                {columns.map((c) => (
                  <td key={c.key} style={{ ...TD, ...(c.num ? NUM : null), ...(c.style || null) }}>
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}

const dash = (v) => v || <span style={{ color: '#B0B0B0' }}>-</span>;
const Note = ({ children }) => <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.5, marginBottom: 12 }}>{children}</div>;
const Empty = ({ children }) => (
  <div style={{ padding: 18, fontSize: 11.5, color: MUTED, textAlign: 'center', border: '1px dashed #E0E0E0', borderRadius: 10 }}>
    {children}
  </div>
);

function QuotedWindow({ data, nameOf, onOpen }) {
  const owners = byOwner(data.rows, nameOf);
  if (!data.rows.length) return <Empty>Nothing was quoted this week.</Empty>;
  return (
    <>
      <RecordTable
        label="Quoted"
        onOpen={onOpen}
        rows={data.rows.map((r) => ({ ...r, key: r.id, href: recordHash(r), title: r.name || r.quote }))}
        columns={[
          { key: 'quote', label: 'Quote #', cell: (r) => <span style={MONO}>{dash(r.quote)}</span> },
          { key: 'customer', label: 'Customer', cell: (r) => dash(r.customer) },
          { key: 'name', label: 'Name', cell: (r) => <span style={{ fontWeight: 600, color: '#0D0D0D' }}>{dash(r.name)}</span> },
          { key: 'owner', label: 'Owner', cell: (r) => dash(r.owner && nameOf(r.owner)) },
          { key: 'price', label: 'Quoted price', num: true, cell: (r) => money(r.quotedPrice) },
          { key: 'status', label: 'Status', cell: (r) => dash(r.status) },
          { key: 'date', label: 'Quote date', style: { whiteSpace: 'nowrap' }, cell: (r) => fmtDate(r.quoteDate) },
        ]}
        footer={
          <tr>
            <td style={TOTAL} colSpan={4}>
              Total · {plural(data.count, 'quote')}
            </td>
            <td style={{ ...TOTAL, ...NUM }}>{money(data.total)}</td>
            <td style={TOTAL} colSpan={2} />
          </tr>
        }
      />
      <div
        style={{ fontSize: 9.5, fontWeight: 800, color: '#5A5A5A', letterSpacing: 1.2, textTransform: 'uppercase', margin: '16px 0 8px' }}
      >
        By owner
      </div>
      <div style={{ border: '1px solid #ECECEC', borderRadius: 10, overflow: 'hidden', maxWidth: 460 }}>
        <table aria-label="Quoted by owner" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Owner</th>
              <th style={{ ...TH, textAlign: 'right' }}>Quotes</th>
              <th style={{ ...TH, textAlign: 'right' }}>Quoted</th>
            </tr>
          </thead>
          <tbody>
            {owners.map((g) => (
              <tr key={g.owner}>
                <td style={{ ...TD, fontWeight: 600 }}>{g.owner}</td>
                <td style={{ ...TD, ...NUM }}>{g.count}</td>
                <td style={{ ...TD, ...NUM }}>{money(g.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PosWindow({ data, onOpen }) {
  if (!data.rows.length) return <Empty>No POs were issued this week.</Empty>;
  return (
    <RecordTable
      label="PO's"
      onOpen={onOpen}
      rows={data.rows.map((r) => ({ ...r, key: r.id, href: recordHash(r), title: r.name || r.poNumber }))}
      columns={[
        { key: 'po', label: 'PO #', cell: (r) => <span style={MONO}>{dash(r.poNumber)}</span> },
        { key: 'quote', label: 'Quote #', cell: (r) => <span style={MONO}>{dash(r.quote)}</span> },
        { key: 'customer', label: 'Customer', cell: (r) => dash(r.customer) },
        { key: 'name', label: 'Name', cell: (r) => <span style={{ fontWeight: 600, color: '#0D0D0D' }}>{dash(r.name)}</span> },
        { key: 'value', label: 'PO value', num: true, cell: (r) => money(r.poValue) },
        { key: 'date', label: 'PO date', style: { whiteSpace: 'nowrap' }, cell: (r) => fmtDate(r.poDate) },
        { key: 'mgr', label: 'Approval manager', cell: (r) => dash(r.approvalManager) },
      ]}
      footer={
        <tr>
          <td style={TOTAL} colSpan={4}>
            Total · {plural(data.count, 'PO')}
          </td>
          <td style={{ ...TOTAL, ...NUM }}>{money(data.total)}</td>
          <td style={TOTAL} colSpan={2} />
        </tr>
      }
    />
  );
}

const invoiceDay = (at) => fmtDate(dayIn(new Date(at), DASH_TIME_ZONE));
// a ledger invoice carries its own date; an older answer only the time it was saved
const dateOf = (r) => (r.date ? fmtDate(r.date) : invoiceDay(r.at));

function InvoicesWindow({ invoices, onOpen }) {
  const note = (
    <Note>
      The invoices in the invoice ledger whose invoice date is in the week - entered on the Finance Invoice tab or with &quot;Add an
      invoice&quot; on a project. Opening balances carried over from before the ledger are not counted. GLOBAL KSA records are not included.
    </Note>
  );
  if (invoices.state === 'loading')
    return (
      <>
        {note}
        <Empty>Loading…</Empty>
      </>
    );
  if (invoices.state !== 'ok') {
    return (
      <>
        {note}
        <Empty>
          {invoices.state === 'denied' ? 'Your access does not include invoicing.' : 'Could not load the invoices - reload to try again.'}
        </Empty>
      </>
    );
  }
  const { data } = invoices;
  const sum = invoiceSummary(data);
  return (
    <>
      {note}
      {data.invoices.length === 0 ? (
        <Empty>No invoices were entered this week.</Empty>
      ) : (
        <RecordTable
          label="Invoices"
          onOpen={onOpen}
          rows={data.invoices.map((r) => ({
            ...r,
            key: r.id || r.auditId,
            title: r.name || r.quoteLetter,
            href: r.deleted ? null : recordHash({ id: r.projectId, status: r.status }),
          }))}
          columns={[
            {
              key: 'numbers',
              label: 'Invoice #',
              cell: (r) => <span style={MONO}>{r.numbers.length ? r.numbers.join(', ') : dash('')}</span>,
            },
            { key: 'quote', label: 'Quote #', cell: (r) => <span style={MONO}>{dash(r.quoteLetter)}</span> },
            { key: 'customer', label: 'Customer', cell: (r) => dash(r.customer) },
            {
              key: 'name',
              label: 'Name',
              cell: (r) => (
                <span style={{ fontWeight: 600, color: '#0D0D0D' }}>
                  {dash(r.name)}
                  {r.deleted ? <span style={{ color: MUTED, fontWeight: 500 }}> (deleted)</span> : null}
                </span>
              ),
            },
            { key: 'po', label: 'PO #', cell: (r) => <span style={MONO}>{dash(r.poNumber)}</span> },
            { key: 'amount', label: 'Amount', num: true, cell: (r) => money(r.amount) },
            { key: 'date', label: 'Date', style: { whiteSpace: 'nowrap' }, cell: (r) => dateOf(r) },
            { key: 'by', label: 'Entered by', cell: (r) => dash(r.actor) },
          ]}
          footer={
            <tr>
              <td style={TOTAL} colSpan={5}>
                Total · {plural(sum.count, 'invoice')}
              </td>
              <td style={{ ...TOTAL, ...NUM }}>{money(sum.total)}</td>
              <td style={TOTAL} colSpan={2} />
            </tr>
          }
        />
      )}
      {sum.corrections > 0 && (
        <div style={{ marginTop: 12, fontSize: 11, color: MUTED, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700 }}>
            Also {plural(sum.corrections, 'save')} lowered an invoiced total, by {money(-sum.correctionTotal)} in all (a typo put right, or
            an entry undone). Not subtracted above:
          </div>
          {data.corrections.map((c) => (
            <div key={c.auditId}>
              {[c.quoteLetter && `Q${c.quoteLetter}`, c.customer, c.name].filter(Boolean).join(' · ') || c.projectId} - {money(-c.amount)}{' '}
              on {invoiceDay(c.at)}
              {c.actor ? ` by ${c.actor}` : ''}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function CollectedWindow({ collected, onOpen }) {
  const note = (
    <Note>
      The payments recorded against invoices on the Finance Invoice tab, by the day each was received. GLOBAL KSA records are not included.
    </Note>
  );
  if (collected.state === 'loading')
    return (
      <>
        {note}
        <Empty>Loading…</Empty>
      </>
    );
  if (collected.state !== 'ok') {
    return (
      <>
        {note}
        <Empty>
          {collected.state === 'denied' ? 'Your access does not include payments.' : 'Could not load the payments - reload to try again.'}
        </Empty>
      </>
    );
  }
  const { data } = collected;
  const sum = collectedSummary(data);
  if (!data.payments.length)
    return (
      <>
        {note}
        <Empty>No payments were recorded this week.</Empty>
      </>
    );
  return (
    <>
      {note}
      <RecordTable
        label="Payments"
        onOpen={onOpen}
        rows={data.payments.map((p) => ({
          ...p,
          key: p.id,
          title: p.project_name || p.quote_letter,
          href: recordHash({ id: p.project_id, status: p.project_status }),
        }))}
        columns={[
          { key: 'date', label: 'Received', style: { whiteSpace: 'nowrap' }, cell: (r) => fmtDate(r.paid_on) },
          {
            key: 'inv',
            label: 'Invoice #',
            cell: (r) => <span style={MONO}>{dash(r.invoice_number || (r.kind === 'opening' ? 'Opening balance' : ''))}</span>,
          },
          { key: 'customer', label: 'Customer', cell: (r) => dash(r.customer) },
          {
            key: 'name',
            label: 'Project',
            cell: (r) => (
              <span style={{ fontWeight: 600, color: '#0D0D0D' }}>
                {dash([r.quote_letter && `Q${r.quote_letter}`, r.project_name].filter(Boolean).join(' · '))}
              </span>
            ),
          },
          { key: 'how', label: 'Method / ref', cell: (r) => dash([r.method, r.reference].filter(Boolean).join(' · ')) },
          { key: 'amount', label: 'Amount', num: true, cell: (r) => money(r.amount) },
        ]}
        footer={
          <tr>
            <td style={TOTAL} colSpan={5}>
              Total · {plural(sum.count, 'payment')}
            </td>
            <td style={{ ...TOTAL, ...NUM }}>{money(sum.total)}</td>
          </tr>
        }
      />
    </>
  );
}

/* ── the boxes ───────────────────────────────────────────────────────── */

/**
 * { kpis, window } for DashboardView: the KPI entries this person may see
 * (each with `onOpen`) and the open window, or null. `projects` = AppContext
 * allProjects (every year: last week is absolute); `nameOf` turns a stored
 * owner into a name.
 */
/**
 * The week boxes' This / Last switch, on each box's date line. A press never
 * opens the box's window (it is inside the box's button).
 */
function WeekSwitch({ which, onChange }) {
  const btn = (key, text, name) => (
    <button
      key={key}
      type="button"
      aria-pressed={which === key}
      aria-label={name}
      title={`Show ${name.toLowerCase()} in every week box`}
      onClick={(e) => {
        e.stopPropagation();
        onChange(key);
      }}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        border: 'none',
        background: which === key ? '#00695C' : 'transparent',
        color: which === key ? '#fff' : '#4A5A55',
        borderRadius: 10,
        padding: '2px 8px',
        fontSize: 9.5,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
        lineHeight: 1.4,
      }}
    >
      {text}
    </button>
  );
  return (
    <span
      role="group"
      aria-label="Which week the boxes show"
      style={{ display: 'inline-flex', gap: 1, padding: 1, borderRadius: 11, border: '1px solid #D8DED4', background: '#fff' }}
    >
      {btn('this', 'This', 'This week')}
      {btn('last', 'Last', 'Last week')}
    </span>
  );
}

export function useLastWeekBoxes({ projects, nameOf }) {
  const today = dayIn(new Date(), DASH_TIME_ZONE);
  // the boxes show last week (the default) or this week so far - the buttons above them
  const [which, setWhich] = useState('last');
  const lastFrom = lastWeekRange(today).from;
  const thisMonday = addDays(lastFrom, 7);
  const boxFrom = which === 'this' ? thisMonday : lastFrom;
  const range = useMemo(() => ({ from: boxFrom, to: addDays(boxFrom, 6) }), [boxFrom]);
  const W = which === 'this' ? 'this week' : 'last week';
  const access = useMyAccess();
  const level = (area) => (access && access.areas && access.areas[area]) || 'hidden';
  const boxesLevel = level('week-boxes');
  const collectedLevel = level('week-collected');
  const sees = {
    quoted: boxesLevel !== 'hidden',
    pos: boxesLevel !== 'hidden',
    invoices: boxesLevel !== 'hidden',
    collected: collectedLevel !== 'hidden',
  };
  const edits = {
    quoted: boxesLevel === 'edit',
    pos: boxesLevel === 'edit',
    invoices: boxesLevel === 'edit',
    collected: collectedLevel === 'edit',
  };

  // the window's week: last week when it opens, stepped with its arrows
  const [open, setOpen] = useState(null);
  const [shownFrom, setShownFrom] = useState(range.from);
  const shown = useMemo(() => ({ from: shownFrom, to: addDays(shownFrom, 6) }), [shownFrom]);
  const same = shown.from === range.from;

  const quoted = useMemo(() => quotedIn(projects, range), [projects, range]);
  const pos = useMemo(() => posIn(projects, range), [projects, range]);
  const invoices = useInvoices(range, !sees.invoices);
  const figs = useWeekFigures(range.from, !sees.invoices && !sees.collected);
  const collected = useCollected(range, !sees.collected);
  const shownCollected = useCollected(shown, same || !open || !sees.collected);
  const shownInvoices = useInvoices(shown, same || !open || !sees.invoices);
  const shownFigs = useWeekFigures(shown.from, same || !open);
  const ownerName = useCallback((o) => (nameOf && String(o || '').includes('@') ? nameOf(o) || o : o), [nameOf]);

  const when = rangeLabel(range);
  const longWhen = rangeLongLabel(range);
  const tooltip =
    which === 'this'
      ? `This week so far: ${longWhen}. The year filter does not apply. Click for the breakdown.`
      : `Last week: ${longWhen}. Always the last full Monday-Sunday week - the year filter does not apply. Click for the breakdown.`;
  const openBox = (key) => {
    setShownFrom(range.from);
    setOpen(key);
  };
  // every box carries the This / Last switch on its date line, so it moves
  // with the boxes wherever the person puts them; one press switches them all
  const footer = <WeekSwitch which={which} onChange={setWhich} />;
  const common = (key, extra) => ({
    key,
    yearLabel: when,
    title: tooltip,
    onOpen: () => openBox(key),
    footer,
    accent: '#00695C',
    iconBg: 'rgba(0,105,92,0.08)',
    ...extra,
  });
  const sum = invoices.state === 'ok' ? invoiceSummary(invoices.data) : null;
  const got = collected.state === 'ok' ? collectedSummary(collected.data) : null;
  const f = figs.figures;
  const entered = (fig) => (fig ? ` · entered by ${fig.setBy || 'someone'}` : '');
  const byHand = 'Numbers entered by hand for the week replace the count.';

  const all = [
    common('quoted', {
      // the right-hand column of the row: its explainer opens leftwards
      label: `Quoted ${W}`,
      icon: 'target',
      value: f.quoted ? f.quoted.count : quoted.count,
      explainAlign: 'right',
      sub: `${money(f.quoted ? f.quoted.total : quoted.total)} quoted${entered(f.quoted)}`,
      explain: {
        title: `Quoted ${W}`,
        body: `Opportunities and projects (any status) whose quote date is in ${W}, ${longWhen}. The year filter does not apply. ${byHand}`,
        formula: 'COUNT(*), SUM(quoted_price)\n  WHERE quote_date is in last week',
      },
    }),
    common('pos', {
      label: `PO's ${W}`,
      icon: 'check',
      value: f.pos ? f.pos.count : pos.count,
      sub: `${money(f.pos ? f.pos.total : pos.total)} in PO value${entered(f.pos)}`,
      explain: {
        title: `PO's ${W}`,
        body: `Records with a PO # whose PO date is in ${W}, ${longWhen}. The year filter does not apply. ${byHand}`,
        formula: 'COUNT(*), SUM(po_value)\n  WHERE po_date_issued is in the week\n  AND po_number is not blank',
      },
    }),
    common(
      'collected',
      f.collected
        ? {
            label: `$ collected ${W}`,
            icon: 'dollar',
            value: money(f.collected.total),
            sub: `Entered by ${f.collected.setBy || 'someone'}`,
          }
        : {
            label: `$ collected ${W}`,
            icon: 'dollar',
            value: got ? money(got.total) : collected.state === 'loading' ? '…' : '-',
            muted: !got,
            sub: got ? `${plural(got.count, 'payment')} received` : collected.state === 'loading' ? 'Loading…' : 'Could not load',
            explain: {
              title: `$ collected ${W}`,
              body: `The payments received in ${W}, ${longWhen}, as recorded against invoices on the Finance Invoice tab. GLOBAL KSA records are not included. ${byHand.replace('Numbers', 'An amount')}`,
              formula: 'SUM(invoice_payments.amount)\n  WHERE paid_on is in the week',
            },
          }
    ),
    common('invoices', {
      label: `Invoices ${W}`,
      icon: 'checklist',
      explainAlign: 'right',
      value: f.invoices ? f.invoices.count : sum ? sum.count : invoices.state === 'loading' ? '…' : '-',
      muted: !f.invoices && !sum,
      sub: f.invoices
        ? `${money(f.invoices.total)} invoiced${entered(f.invoices)}`
        : sum
          ? `${money(sum.total)} invoiced`
          : invoices.state === 'loading'
            ? 'Loading…'
            : 'Could not load',
      explain: {
        title: `Invoices ${W}`,
        body: `The invoices in the invoice ledger dated in ${W}, ${longWhen} (the Finance Invoice tab, and "Add an invoice" on a project). Opening balances from before the ledger and void invoices are not counted. GLOBAL KSA records are not included. ${byHand}`,
        formula: 'COUNT(*), SUM(amount) FROM invoice_ledger\n  WHERE invoice_date is in the week\n  AND not void, not an opening balance',
      },
    }),
  ];
  const kpis = all.filter((k) => sees[k.key]);

  // the window, for its own week
  const wQuoted = same ? quoted : quotedIn(projects, shown);
  const wPos = same ? pos : posIn(projects, shown);
  const wInvoices = same ? invoices : shownInvoices;
  const wFigs = same ? figs : shownFigs;
  const wSum = wInvoices.state === 'ok' ? invoiceSummary(wInvoices.data) : null;
  const wCollected = same ? collected : shownCollected;
  const wGot = wCollected.state === 'ok' ? collectedSummary(wCollected.data) : null;
  const reloadFigs = async () => {
    await figs.reload();
    if (!same) await shownFigs.reload();
  };
  const weekWord = shown.from === thisMonday ? 'this week so far' : shown.from === lastFrom ? 'last week' : 'an earlier week';

  const go = (href) => {
    setOpen(null);
    window.location.hash = href;
  };
  const titles = { quoted: 'Quoted', pos: "PO's", collected: '$ collected', invoices: 'Invoices' };
  const counted = {
    quoted: { count: wQuoted.count, total: wQuoted.total },
    pos: { count: wPos.count, total: wPos.total },
    invoices: wSum && { count: wSum.count, total: wSum.total },
    collected: wGot && { count: wGot.count, total: wGot.total },
  };
  const words = { quoted: 'quote', pos: 'PO', invoices: 'invoice', collected: 'payment' };
  const arrow = { ...INPUT, cursor: 'pointer', background: '#fff', padding: '3px 9px', fontWeight: 700 };
  const atEnd = shown.from >= thisMonday;
  const window_ = open && sees[open] && (
    <Modal open onClose={() => setOpen(null)} title={`${titles[open]} · ${rangeLabel(shown)}`} width={1040}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <button type="button" aria-label="The week before" onClick={() => setShownFrom(addDays(shown.from, -7))} style={arrow}>
          ‹
        </button>
        <button
          type="button"
          aria-label="The week after"
          disabled={atEnd}
          onClick={() => setShownFrom(addDays(shown.from, 7))}
          style={{ ...arrow, opacity: atEnd ? 0.35 : 1 }}
        >
          ›
        </button>
        <span style={{ fontSize: 11, color: MUTED }}>
          {rangeLongLabel(shown)} - {weekWord}
          {open !== 'collected' ? "; the Dashboard's year filter does not apply" : ''}.
        </span>
      </div>
      <FigureBar
        key={`${open}-${shown.from}`}
        box={open}
        week={shown.from}
        figure={wFigs.figures[open] || null}
        counted={counted[open]}
        canEdit={edits[open]}
        countWord={words[open]}
        onSaved={reloadFigs}
      />
      {open === 'quoted' && <QuotedWindow data={wQuoted} nameOf={ownerName} onOpen={go} />}
      {open === 'pos' && <PosWindow data={wPos} onOpen={go} />}
      {open === 'collected' && <CollectedWindow collected={wCollected} onOpen={go} />}
      {open === 'invoices' && <InvoicesWindow invoices={wInvoices} onOpen={go} />}
    </Modal>
  );
  return { kpis, window: window_ || null, which };
}
